/// <reference path="./types/trt2__gsm-charset-utils.d.ts" />
import * as GsmCharsetUtils from "@trt2/gsm-charset-utils";

import smartEncodingReplacements from "./smartEncodingReplacements";

export const MMS_MAX_CHARS = 1800;
export const MMS_MAX_CHARS_FOR_WARNING = 1600;
export const SMS_MAX_CHARS = 1600;
export const DEFAULT_AUTO_MMS_BREAKPOINT = 3;

export type MessageInput = {
  text: string;
  hasAttachments: boolean;
};

export type SMSTextEncodingTenant = {
  autoMMSBreakpoint?: number | null | undefined;
};

export function smartEncode(msg: MessageInput): string {
  const text = msg.text;
  if (msg.hasAttachments) {
    // No need to simplify MMS messages; they get sent as UTF-8
    return text;
  }

  // Simplify the message
  let previousSegmentStart = 0;
  // concat using += because it is faster than contact and join
  let out = "";
  for (let i = 0; i < text.length; i++) {
    const replacement = smartEncodingReplacements.get(text[i]);
    if (replacement) {
      if (previousSegmentStart === i) {
        // two replacements in a row, don't bother pushing an empty text.slice()
        out += replacement;
      } else {
        out += text.slice(previousSegmentStart, i) + replacement;
      }
      previousSegmentStart = i + 1;
    }
  }

  if (previousSegmentStart === 0) {
    // we encountered no replacements, so just return the string unaltered
    return text;
  }

  const lastSegment = text.slice(previousSegmentStart, text.length);
  if (lastSegment) {
    out += lastSegment;
  }

  return out;
}

export type EncodingStats = {
  smartEncoded: string;
  charset: "GSM" | "UCS2" | "UTF-8";
  segments: number;
  charCount: number;
  charsPerSegment: number;
  charsLeftInSegment: number;
  maxChars: number;
  maxCharsForWarning: number;
  sendAsMessageType: "SMS" | "MMS";
};

export function getEncodingStats(
  msg: MessageInput,
  tenant: SMSTextEncodingTenant
): EncodingStats {
  if (msg.hasAttachments) {
    // MMS
    return {
      smartEncoded: msg.text,
      charset: "UTF-8",
      segments: 1,
      charCount: msg.text.length,
      charsPerSegment: MMS_MAX_CHARS,
      charsLeftInSegment: MMS_MAX_CHARS - msg.text.length,
      maxChars: MMS_MAX_CHARS,
      maxCharsForWarning: MMS_MAX_CHARS_FOR_WARNING,
      sendAsMessageType: "MMS",
    };
  }

  const smartEncoded = smartEncode(msg);
  const info = GsmCharsetUtils.getCharCount(smartEncoded);

  if (
    info.msgCount >= (tenant.autoMMSBreakpoint || DEFAULT_AUTO_MMS_BREAKPOINT)
  ) {
    // SMS-as-MMS
    return {
      // discard the smart-encoded version; we don't need to smart-encode
      // MMS (we just needed to run that to see if it would fit under the
      // breakpoint after smart-encoding)
      smartEncoded: msg.text,
      charset: "UTF-8",
      segments: 1,
      charCount: msg.text.length,
      charsPerSegment: MMS_MAX_CHARS,
      charsLeftInSegment: MMS_MAX_CHARS - msg.text.length,
      maxChars: MMS_MAX_CHARS,
      maxCharsForWarning: MMS_MAX_CHARS_FOR_WARNING,
      sendAsMessageType: "MMS",
    };
  }

  return {
    smartEncoded,
    charset: info.encoding,
    segments: info.msgCount,
    charCount: info.charCount,
    charsPerSegment: info.charsPerSegment,
    charsLeftInSegment: info.charsPerSegment * info.msgCount - info.charCount,
    maxChars: SMS_MAX_CHARS,
    maxCharsForWarning: SMS_MAX_CHARS,
    sendAsMessageType: "SMS",
  };
}
